import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import {
    FLEET_ACTIVE_OPTIONS,
    FLEET_TRAILER_STATUS_OPTIONS,
    FleetTrailer,
    FleetTrailerType
} from '../models/trailer.model';
import { FleetTrailerService } from '../trailer.service';

@Component({
    selector: 'app-trailer-form',
    standalone: true,
    templateUrl: './trailer-form.component.html',
    styleUrls: ['../trailer-page.css'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        DatePickerModule,
        InputTextModule,
        SelectModule,
        TextareaModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent
    ]
})
export class TrailerFormComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    form!: FormGroup;
    trailerId?: number;
    existing?: FleetTrailer;
    trailerTypes: FleetTrailerType[] = [];
    isLoading = false;
    isSaving = false;
    readonly activeOptions = FLEET_ACTIVE_OPTIONS;
    readonly statusOptions = FLEET_TRAILER_STATUS_OPTIONS;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private trailerService: FleetTrailerService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.trailerId = Number(this.route.snapshot.paramMap.get('id')) || undefined;
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Trailers', routerLink: '/fleet/trailers' },
            { label: this.trailerId ? 'Edit' : 'Create' }
        ];
        this.form = this.fb.group({
            assetNumber: ['', Validators.required],
            trailerType: [null, Validators.required],
            plateNumber: [''],
            registrationNumber: [''],
            registrationExpiry: [null],
            insuranceExpiry: [null],
            status: ['AVAILABLE', Validators.required],
            active: [true, Validators.required],
            notes: ['']
        });
        this.loadTrailerTypes();
        if (this.trailerId) {
            this.loadTrailer(this.trailerId);
        }
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.messageService.add({ severity: 'warn', summary: 'Missing details', detail: 'Asset number and trailer type are required.' });
            return;
        }

        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        const trailerType = this.form.value.trailerType as FleetTrailerType;
        this.isSaving = true;
        const payload: FleetTrailer = {
            ...this.existing,
            trailerId: this.trailerId,
            owningCompanyId: companyId,
            transportationAgencyId: this.transportationAgencyId(),
            assetNumber: this.form.value.assetNumber,
            trailerTypeId: trailerType.trailerTypeId,
            trailerTypeCode: trailerType.code,
            trailerTypeName: trailerType.name,
            plateNumber: this.form.value.plateNumber,
            registrationNumber: this.form.value.registrationNumber,
            registrationExpiry: this.form.value.registrationExpiry,
            insuranceExpiry: this.form.value.insuranceExpiry,
            status: this.form.value.status,
            active: this.form.value.active,
            notes: this.form.value.notes
        };
        const request = this.trailerId
            ? this.trailerService.updateTrailer(this.trailerId, payload)
            : this.trailerService.createTrailer(payload);

        request.subscribe({
            next: response => {
                this.isSaving = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Trailer saved.' });
                this.router.navigate(['/fleet/trailer/view', response.data.trailerId]);
            },
            error: error => {
                this.isSaving = false;
                this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message || 'Could not save trailer.' });
            }
        });
    }

    cancel(): void {
        if (this.trailerId) {
            this.router.navigate(['/fleet/trailer/view', this.trailerId]);
            return;
        }
        this.router.navigate(['/fleet/trailers']);
    }

    title(): string {
        return this.trailerId ? 'Edit Trailer' : 'New Trailer';
    }

    selectedTrailerTypeName(): string {
        const trailerType = this.form?.value.trailerType as FleetTrailerType | null;
        return trailerType?.name || this.existing?.trailerTypeName || '-';
    }

    private loadTrailerTypes(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            return;
        }
        this.trailerService.searchTrailerTypes({ owningCompanyId: companyId, page: 0, pageSize: 250, active: true })
            .subscribe({
                next: response => {
                    this.trailerTypes = response.items ?? [];
                    if (this.existing) {
                        this.patchTrailerType();
                    }
                },
                error: () => this.messageService.add({ severity: 'warn', summary: 'Trailer types', detail: 'Could not load active trailer types.' })
            });
    }

    private loadTrailer(id: number): void {
        this.isLoading = true;
        this.trailerService.getTrailer(id).subscribe({
            next: response => {
                this.existing = response.data;
                this.form.patchValue({
                    assetNumber: response.data.assetNumber ?? '',
                    plateNumber: response.data.plateNumber ?? '',
                    registrationNumber: response.data.registrationNumber ?? '',
                    registrationExpiry: response.data.registrationExpiry ? new Date(response.data.registrationExpiry) : null,
                    insuranceExpiry: response.data.insuranceExpiry ? new Date(response.data.insuranceExpiry) : null,
                    status: response.data.status ?? 'AVAILABLE',
                    active: response.data.active !== false,
                    notes: response.data.notes ?? ''
                });
                this.patchTrailerType();
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load trailer.' });
            }
        });
    }

    private patchTrailerType(): void {
        if (!this.existing || !this.trailerTypes.length) {
            return;
        }
        const trailerType = this.trailerTypes.find(item => item.trailerTypeId === this.existing?.trailerTypeId) ?? null;
        this.form.patchValue({ trailerType }, { emitEvent: false });
    }

    private transportationAgencyId(): number | undefined {
        return this.sessionStorageService.getCompany()?.transportationAgencyId
            ?? this.sessionStorageService.getTransportationAgency()?.rowId
            ?? undefined;
    }
}
